import type { CallStateCommandShapesWithoutMeta } from "@theirstory/common/src/call_state/commands";

import {
  CALL_FINISH,
  CALL_INITIATE_LOCAL_RECORDING,
  CALL_LOCAL_RECORDING_CAPABILITY_DETERMINED,
  CALL_LOCAL_RECORDING_PART_DONE_RECORDING,
  CALL_LOCAL_RECORDING_PART_UPLOAD_FAILED,
  CALL_LOCAL_RECORDING_PART_UPLOAD_SUCCESS,
  CALL_LOCAL_RECORDING_SINGLE_PART_INFO,
  CALL_LOCAL_RECORDING_UPLOAD_COMPLETION,
  CALL_LOCAL_RECORDING_UPLOAD_FAILED,
  CALL_LOCAL_RECORDING_UPLOAD_PENDING,
  CALL_PARTICIPANT_IDENTIFIED,
  CALL_PARTICIPANT_STARTED_LOCALLY_RECORDING,
  CALL_PARTICIPANT_STORAGE_QUOTA_DETERMINED,
  CALL_STARTED,
  CALL_STOPPED_RECORDING,
  CALL_STOP_LOCAL_RECORDING,
} from "../../actions/types";
import type { LocalRecordingStreamPartMetadata } from "../../../lib/localdbNew";
import { LocalMediaStream } from "../../../lib/localRecordingNew";
import type { PartUploadProgressEvent } from "../../../lib/localRecordingNew";
import type { RootState } from "../rootReducer";

export function generateCallStateCommands(state: RootState, action: { type: string; data: any }) {
  const commands: CallStateCommandShapesWithoutMeta[] = [];

  switch (action.type) {
    case CALL_STARTED: {
      // Probably JOIN_CALL
      commands.push({
        type: "JOIN_CALL",
        payload: {
          name: state.auth?.full_name || state.call?.participants?.get(state.auth?.visitorId)?.name || "unknown",
        },
      });
      break;
    }
    case CALL_PARTICIPANT_STORAGE_QUOTA_DETERMINED: {
      const payload: StorageEstimate = action.data;

      commands.push({
        type: "DETERMINE_PARTICIPANT_QUOTA",
        payload: {
          bytesAvailable: payload.quota,
          bytesUsed: payload.usage,
        },
      });
      break;
    }
    case CALL_LOCAL_RECORDING_CAPABILITY_DETERMINED: {
      commands.push({
        type: "DETERMINE_LOCAL_RECORDING_CAPABILITY",
        payload: {
          capability: action.data,
        },
      });
      break;
    }
    case CALL_FINISH: {
      // Probably LEAVE_CALL
      commands.push({
        type: "LEAVE_CALL",
        payload: null,
      });
      break;
    }
    case CALL_PARTICIPANT_IDENTIFIED: {
      // Probably PARTICIPANT_NAME_SET
      commands.push({
        type: "SET_PARTICIPANT_NAME",
        payload: {
          // I love consistency
          name: (action as any).payload || "unknown",
        },
      });
      break;
    }
    case CALL_INITIATE_LOCAL_RECORDING: {
      // Probably START_CALL_LOCAL_RECORDING
      commands.push({
        type: "START_CALL_LOCAL_RECORDING",
        payload: null,
      });
      break;
    }
    case CALL_STOPPED_RECORDING: {
      // Probably STOP_CALL_LOCAL_RECORDING
      if ((action as any).payload.source === "user") {
        commands.push({
          type: "STOP_CALL_LOCAL_RECORDING",
          payload: null,
        });
      }
      break;
    }
    case CALL_PARTICIPANT_STARTED_LOCALLY_RECORDING: {
      // Probably START_PARTICIPANT_LOCAL_RECORDING
      console.log("START_PARTICIPANT_LOCAL_RECORDING", action.data);
      const {
        width: videoWidth,
        height: videoHeight,
        ...videoSettings
      }: MediaTrackSettings = action.data.videoSettings;
      const { sampleRate: audioSampleRate, ...audioSettings }: MediaTrackSettings = action.data.audioSettings;
      const videoLabel: string = action.data.videoLabel;
      const audioLabel: string = action.data.audioLabel;
      const videoBitrate: number = action.data.videoBitrate;
      const audioBitrate: number = action.data.audioBitrate;

      commands.push({
        type: "START_PARTICIPANT_LOCAL_RECORDING",
        payload: {
          recordingId: action.data.recordingId,
          video: {
            trackId: state.call.videoSource || action.data.videoSource,
            bitrate: videoBitrate,
            deviceName: videoLabel,
            resolution: {
              width: videoWidth,
              height: videoHeight,
            },
            extra: { ...videoSettings },
          },
          audio: {
            trackId: state.call.audioSource || action.data.audioSource,
            bitrate: audioBitrate,
            deviceName: audioLabel,
            sampleRate: audioSampleRate,
            extra: { ...audioSettings },
          },
        },
      });
      break;
    }
    case CALL_STOP_LOCAL_RECORDING: {
      // Dispatched both when we stop the recording, and when someone else does
      // So we're going to treat it as STOP_PARTICIPANT_LOCAL_RECORDING
      commands.push({
        type: "STOP_PARTICIPANT_LOCAL_RECORDING",
        payload: {
          recordingId: action.data.recordingId,
        },
      });
      break;
    }
    default:
      break;
  }

  return commands;
}

export function generateCallLocalRecordingCommands(action: {
  type: string;
  data: {
    stream: LocalMediaStream;
    [key: string]: any;
  };
}) {
  const commands: CallStateCommandShapesWithoutMeta[] = [];

  switch (action.type) {
    case CALL_LOCAL_RECORDING_SINGLE_PART_INFO: {
      // Probably SEND_LOCAL_RECORDING_PART_INFO, but just one
      const part: LocalRecordingStreamPartMetadata = action.data.part;
      commands.push({
        type: "SEND_LOCAL_RECORDING_PART_INFO",
        payload: {
          partId: part.partId,
          byteLength: part.byteLength,
          recordingId: part.streamId,
          recording: action.data.isRecording,
        },
      });

      if (part.uploaded) {
        commands.push({
          type: "SEND_LOCAL_RECORDING_PART_UPLOAD_COMPLETED",
          payload: {
            partId: part.partId,
          },
        });
      }
      break;
    }
    case CALL_LOCAL_RECORDING_PART_DONE_RECORDING: {
      commands.push({
        type: "SEND_LOCAL_RECORDING_PART_DONE_RECORDING",
        payload: {
          partId: action.data.partId,
        },
      });
      break;
    }
    case CALL_LOCAL_RECORDING_UPLOAD_PENDING: {
      // Probably SEND_LOCAL_RECORDING_PART_UPLOAD_PROGRESS
      const progressEvent: PartUploadProgressEvent | LocalMediaStream = action.data as any;

      if (
        !(progressEvent instanceof LocalMediaStream) &&
        !!progressEvent.partId &&
        typeof progressEvent.uploadedBytes === "number"
      ) {
        commands.push({
          type: "SEND_LOCAL_RECORDING_PART_UPLOAD_PROGRESS",
          payload: {
            partId: progressEvent.partId,
            uploadedBytes: progressEvent.uploadedBytes,
          },
        });
      }

      break;
    }
    case CALL_LOCAL_RECORDING_PART_UPLOAD_SUCCESS: {
      // Probably SEND_LOCAL_RECORDING_PART_UPLOAD_COMPLETED
      commands.push({
        type: "SEND_LOCAL_RECORDING_PART_UPLOAD_COMPLETED",
        payload: {
          partId: action.data.partId,
        },
      });
      break;
    }
    case CALL_LOCAL_RECORDING_PART_UPLOAD_FAILED: {
      // Probably SEND_LOCAL_RECORDING_PART_UPLOAD_FAILED
      commands.push({
        type: "SEND_LOCAL_RECORDING_PART_UPLOAD_FAILED",
        payload: {
          partId: action.data.partId,
          message: action.data.message,
        },
      });
      break;
    }
    case CALL_LOCAL_RECORDING_UPLOAD_FAILED: {
      // Probably SEND_LOCAL_RECORDING_UPLOAD_FAILED
      commands.push({
        type: "SEND_LOCAL_RECORDING_UPLOAD_FAILED",
        payload: {
          recordingId: action.data.stream.id,
          message: action.data.message,
        },
      });
      break;
    }
    case CALL_LOCAL_RECORDING_UPLOAD_COMPLETION: {
      // Probably SEND_LOCAL_RECORDING_UPLOAD_COMPLETED
      commands.push({
        type: "SEND_LOCAL_RECORDING_UPLOAD_COMPLETED",
        payload: {
          recordingId: action.data.stream.id,
        },
      });
      break;
    }
    default:
      break;
  }

  return commands;
}
